import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useHistory } from 'react-router-dom'
import authStore from '../../store/authStore'
import mechaLogoSmall from '../../assets/images/mecha_logo_small.png'
import { ChatMessageItem } from './chatTypes'
import ChatChart from './ChatChart'

export function displayNameInitials(name: string | undefined | null): string {
  if (!name || !String(name).trim()) return '?'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

function tryParseJsonArray(text: string): Record<string, unknown>[] | null {
  const trimmed = text.trim()
  if (!trimmed.startsWith('[')) return null
  try {
    const parsed = JSON.parse(trimmed) as unknown
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'object' && parsed[0] !== null) {
      return parsed as Record<string, unknown>[]
    }
  } catch {
    // not valid JSON
  }
  return null
}

function isInternalAppHref(href?: string): boolean {
  if (!href) return false
  if (href.startsWith('data:') || href.startsWith('blob:')) return false
  return href.startsWith('/') && !href.startsWith('//')
}

/** A ref whose href is an inline file (CSV export) the browser should download. */
function isDownloadHref(href?: string): boolean {
  if (!href) return false
  return href.startsWith('data:') || href.startsWith('blob:')
}

interface MarkdownLinkProps {
  href?: string
  children?: React.ReactNode
  onInternalNavigate?: () => void
}

function MarkdownLink({ href, children, onInternalNavigate }: MarkdownLinkProps) {
  const history = useHistory()
  const internal = isInternalAppHref(href)

  return (
    <a
      href={href}
      onClick={(e) => {
        if (!internal || !href) return
        e.preventDefault()
        history.push(href)
        if (onInternalNavigate) onInternalNavigate()
      }}
      {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
    >
      {children}
    </a>
  )
}

interface DataTableProps {
  data: Record<string, unknown>[]
  columns?: string[]
  columnLabels?: Record<string, string>
}

function DataTable({ data, columns, columnLabels }: DataTableProps) {
  const keys = columns && columns.length > 0 ? columns : Object.keys(data[0])
  const formatHeader = (key: string) =>
    (columnLabels && columnLabels[key]) ||
    key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <div className="data-table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            {keys.map((col) => (
              <th key={col}>{formatHeader(col)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={i}>
              {keys.map((col) => (
                <td key={col}>
                  {row[col] === null || row[col] === undefined ? '—' : String(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface RefChipsProps {
  refs: NonNullable<ChatMessageItem['refs']>
  onNavigate?: () => void
}

function RefChips({ refs, onNavigate }: RefChipsProps) {
  const history = useHistory()
  if (!refs.length) return null
  return (
    <div className="chat-ref-chips">
      {refs.map((r, i) => {
        const key = `${r.kind}-${r.id}-${i}`
        // CSV export (or any inline file): trigger a browser download.
        if (r.href && isDownloadHref(r.href)) {
          return (
            <a
              key={key}
              className={`chat-ref-chip ${r.kind}`}
              href={r.href}
              download={r.kind === 'export' ? 'result.csv' : ''}
            >
              {r.label}
            </a>
          )
        }
        // Internal deep link: client-side navigation.
        if (r.href && isInternalAppHref(r.href)) {
          return (
            <a
              key={key}
              className={`chat-ref-chip ${r.kind}`}
              href={r.href}
              onClick={(e) => {
                e.preventDefault()
                history.push(r.href as string)
                onNavigate?.()
              }}
            >
              {r.label}
            </a>
          )
        }
        return (
          <span key={key} className={`chat-ref-chip ${r.kind}`}>
            {r.label}
          </span>
        )
      })}
    </div>
  )
}

interface ChatMessageProps {
  message: ChatMessageItem
  onNavigate?: () => void
}

export function ChatBotAvatar() {
  return (
    <div className="message-avatar bot">
      <img src={mechaLogoSmall} alt="" />
    </div>
  )
}

function ChatMessage({ message, onNavigate }: ChatMessageProps) {
  const { text, sender, timestamp, pending, blocks, refs } = message
  const userInitials = displayNameInitials(authStore.currentUser?.name)
  const showBotLoading = sender === 'bot' && pending === true && !text.trim()
  const hasBlocks = sender === 'bot' && !showBotLoading && Array.isArray(blocks) && blocks.length > 0

  const formatTime = (isoString: string) => {
    const date = new Date(isoString)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const jsonData = sender === 'bot' && !showBotLoading ? tryParseJsonArray(text) : null
  const hasMarkdownTable = sender === 'bot' && /\|[^\n]*---[^\n]*\|/.test(String(text ?? ''))

  const markdownComponents: { [key: string]: React.ComponentType<any> } = {
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <MarkdownLink href={href} onInternalNavigate={onNavigate}>
        {children}
      </MarkdownLink>
    ),
  }

  const hasChart = hasBlocks && blocks!.some((b) => b.type === 'chart')

  return (
    <div
      className={`chat-message ${sender} ${
        jsonData || hasBlocks || hasMarkdownTable || hasChart ? 'wide' : ''
      } ${hasChart ? 'has-chart' : ''}`}
    >
      <div className={`message-avatar ${sender}`}>
        {sender === 'user' ? (
          userInitials
        ) : (
          <img src={mechaLogoSmall} alt="" />
        )}
      </div>
      <div className="message-content">
        <div className="message-bubble">
          {showBotLoading ? (
            <div className="loading-indicator">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
          ) : hasBlocks ? (
            <div className="markdown-content">
              {blocks!.map((block, i) =>
                block.type === 'table' && block.rows && block.rows.length > 0 ? (
                  <DataTable
                    key={i}
                    data={block.rows}
                    columns={block.columns}
                    columnLabels={block.column_labels}
                  />
                ) : block.type === 'chart' ? (
                  <ChatChart key={i} block={block} />
                ) : block.type === 'markdown' && block.text ? (
                  <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {block.text}
                  </ReactMarkdown>
                ) : null
              )}
            </div>
          ) : jsonData ? (
            <DataTable data={jsonData} />
          ) : sender === 'bot' ? (
            <div className="markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {String(text ?? '')}
              </ReactMarkdown>
            </div>
          ) : (
            text
          )}
          {refs && refs.length > 0 && <RefChips refs={refs} onNavigate={onNavigate} />}
        </div>
        {!showBotLoading && <div className="message-timestamp">{formatTime(timestamp)}</div>}
      </div>
    </div>
  )
}

export default ChatMessage